import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Maximize2,
  Minimize2,
  ScreenShare,
  CheckCircle2,
  Camera,
  Mic,
  Wifi
} from 'lucide-react';
import api from '../../api';
import { useTranscriptRecorder } from '../../utils/useTranscriptRecorder';
import {
  AI_TURN_GRACE_MS,
  advanceInactivity,
  createInactivityState,
  inactivityCountdown as remainingSilenceSeconds,
  resetInactivity,
  resolveInactivityConfig
} from '../../utils/inactivityGuard';
import {
  SPEAKING_HOLD_MS,
  VOICE_POLL_MS,
  createVoiceTracker,
  readVoiceLevel
} from '../../utils/voiceActivity';
import './AvatarModal.css';

// Cooldown durations (in ms) per violation type to prevent repetitive interruptions
const SPOKEN_COOLDOWNS = {
  MULTIPLE_PERSONS_DETECTED: 25000,
  PHONE_DETECTED: 25000,
  HEAD_MOVEMENT_AWAY: 20000,
  NO_PERSON_DETECTED: 12000,
  TAB_SWITCH: 15000,
  DEVTOOLS_BLOCKED: 15000,
  SCREEN_SHARE_STOPPED: 15000,
  FULLSCREEN_EXIT: 15000,
  SILENCE_NUDGE: 10000,
  SILENCE_ADVANCE: 10000,
  COUNSELLOR_ENGAGEMENT: 35000,
  GENERIC: 10000
};

const AvatarModal = ({
  showAvatar,
  setShowAvatar,
  mode,
  avatarUrl,
  setFormData,
  setSubmitted,
  currentSessionId,
  sessionMeta,
  onSessionEnded
}) => {
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [integrityScore, setIntegrityScore] = useState(100);
  const [proctoringEvents, setProctoringEvents] = useState([]);
  const [webcamMinimized, setWebcamMinimized] = useState(false);
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(null);
  const [lockedIframeSrc, setLockedIframeSrc] = useState('');
  const [iframeHidden, setIframeHidden] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(null);
  // The policy comes from the server (/start -> inactivity) so the warning the
  // student sees and the rule the report describes can never drift apart.
  const inactivityConfig = React.useMemo(
    () => resolveInactivityConfig(sessionMeta?.inactivity),
    [sessionMeta]
  );
  const [visionState, setVisionState] = useState({
    faceCount: 0,
    faceBox: null,
    gazeDirection: 'CENTER',
    phoneDetected: false
  });
  const [proctoringMetrics, setProctoringMetrics] = useState({
    tabSwitches: 0,
    headTurns: 0,
    faceAbsence: 0,
    multipleFaces: 0,
    phoneDetected: 0,
    fullscreenExits: 0,
    screenShareStops: 0,
    multiMonitorAlerts: 0
  });

  const totalViolations = Object.values(proctoringMetrics).reduce(
    (acc, curr) => acc + (typeof curr === 'number' ? curr : 0),
    0
  );

  // Live Network RTT (Ping) monitoring
  const [livePing, setLivePing] = useState(null);
  const [livePingStatus, setLivePingStatus] = useState('optimal'); // 'optimal' | 'fair' | 'high' | 'unknown'

  useEffect(() => {
    if (!showAvatar) return;
    let isMounted = true;
    
    const checkPing = async () => {
      try {
        const t0 = performance.now();
        await fetch('/api/public/ping', { cache: 'no-store' });
        const t1 = performance.now();
        if (!isMounted) return;
        const pingMs = Math.round(t1 - t0);
        setLivePing(pingMs);
        if (pingMs < 120) {
          setLivePingStatus('optimal');
        } else if (pingMs <= 250) {
          setLivePingStatus('fair');
        } else {
          setLivePingStatus('high');
        }
      } catch (e) {
        if (isMounted) setLivePingStatus('unknown');
      }
    };

    checkPing();
    const interval = setInterval(checkPing, 8000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [showAvatar]);

  const [isInterviewStarted, setIsInterviewStartedState] = useState(false);
  const isInterviewStartedRef = useRef(false);
  const integrityScoreRef = useRef(100);
  const proctoringMetricsRef = useRef({
    tabSwitches: 0,
    headTurns: 0,
    faceAbsence: 0,
    multipleFaces: 0,
    phoneDetected: 0,
    fullscreenExits: 0,
    screenShareStops: 0,
    multiMonitorAlerts: 0
  });

  const isEndingSessionRef = useRef(false);
  const lastInteractionTimeRef = useRef(Date.now());
  const silencePhaseRef = useRef(0);
  // Accumulated *student* silence, separate from the nudge cadence above (the
  // nudges reset lastInteractionTimeRef, which must never reset this clock).
  const inactivityStateRef = useRef(createInactivityState());
  const lastAgentActivityRef = useRef(0);
  // Microphone activity: a transcript only arrives after a whole utterance, so
  // "is the student actually talking" is measured from the mic itself.
  const voiceTrackerRef = useRef(null);
  const lastStudentVoiceAtRef = useRef(0);
  const beyondCallIdRef = useRef(null);
  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const screenVideoRef = useRef(null);
  const screenCanvasRef = useRef(null);
  const screenStreamRef = useRef(null);
  const lastSpokenRef = useRef({});
  const firstOccurrenceRef = useRef({});
  const isAnalyzingFrameRef = useRef(false);
  const isAnalyzingScreenRef = useRef(false);
  const consecutivePhoneFramesRef = useRef(0);
  const proctoringEventsRef = useRef([]);

  const setIsInterviewStarted = useCallback((val) => {
    const wasStarted = isInterviewStartedRef.current;
    isInterviewStartedRef.current = val;
    setIsInterviewStartedState(val);
    if (val && !wasStarted) {
      lastInteractionTimeRef.current = Date.now();
      silencePhaseRef.current = 0;
      setIntegrityScore(100);
      integrityScoreRef.current = 100;
      setProctoringEvents([]);
      proctoringEventsRef.current = [];
      const initialMetrics = {
        tabSwitches: 0,
        headTurns: 0,
        faceAbsence: 0,
        multipleFaces: 0,
        phoneDetected: 0,
        fullscreenExits: 0,
        screenShareStops: 0,
        multiMonitorAlerts: 0
      };
      setProctoringMetrics(initialMetrics);
      proctoringMetricsRef.current = initialMetrics;
    }
  }, []);

  // Stabilize iframe source: lock once when avatar modal opens to prevent tearing down WebRTC connection
  useEffect(() => {
    if (showAvatar && avatarUrl) {
      setLockedIframeSrc(prev => {
        if (prev) return prev;
        const separator = avatarUrl.includes('?') ? '&' : '?';
        return currentSessionId && !avatarUrl.includes('external_id=')
          ? `${avatarUrl}${separator}external_id=${currentSessionId}`
          : avatarUrl;
      });
    } else if (!showAvatar) {
      setLockedIframeSrc('');
    }
  }, [showAvatar, avatarUrl, currentSessionId]);

  // Reset interview start state, silence watchdog, and integrity metrics whenever a session opens or changes
  useEffect(() => {
    if (showAvatar) {
      setIsInterviewStarted(mode !== 'interviewer');
      lastInteractionTimeRef.current = Date.now();
      silencePhaseRef.current = 0;
      inactivityStateRef.current = createInactivityState(inactivityConfig);
      lastAgentActivityRef.current = Date.now();
      lastStudentVoiceAtRef.current = 0;
      voiceTrackerRef.current = null;
      setInactivityCountdown(null);
      setIframeHidden(false);
      setIntegrityScore(100);
      integrityScoreRef.current = 100;
      setProctoringEvents([]);
      proctoringEventsRef.current = [];
      lastSpokenRef.current = {};
      firstOccurrenceRef.current = {};
      consecutivePhoneFramesRef.current = 0;
      setVisionState({ faceCount: 0, faceBox: null, gazeDirection: 'CENTER', phoneDetected: false });
      const initialMetrics = {
        tabSwitches: 0,
        headTurns: 0,
        faceAbsence: 0,
        multipleFaces: 0,
        phoneDetected: 0,
        fullscreenExits: 0,
        screenShareStops: 0,
        multiMonitorAlerts: 0
      };
      setProctoringMetrics(initialMetrics);
      proctoringMetricsRef.current = initialMetrics;
    }
  }, [showAvatar, currentSessionId, mode, setIsInterviewStarted, inactivityConfig]);

  const aiSpeakerLabel = mode === 'interviewer' ? 'EdVergenceX AI Interviewer' : mode === 'counsellor' ? 'AI Counsellor' : 'AI Tutor';

  // The server closes the session when its deadline passes; surface that
  // instead of silently dropping transcript lines.
  const handleSessionExpired = useCallback((detail) => {
    setSessionExpiredNotice(
      detail || 'Interview time limit reached. Your session has been ended.'
    );
  }, []);

  const {
    finalizeSession,
    recordTranscriptLine
  } = useTranscriptRecorder({
    // Tutor & Counsellor modes included: mic capture feeds the transcript the report is generated from
    isSessionActive: showAvatar && (mode === 'interviewer' || mode === 'counsellor' || mode === 'tutor'),
    currentSessionId,
    mode,
    aiSpeakerLabel,
    beyAgentId: sessionMeta?.beyAgentId || '',
    onSessionExpired: handleSessionExpired,
    onSpeechDetected: () => {
      // Do not automatically start interview on ambient microphone speech before avatar conversation has started
    }
  });

  // Proactive speech engine: Avatar directly delivers spoken warnings and conversational nudges
  const speakAvatarInterviewer = useCallback((spokenText, vType = 'GENERIC', isWarning = false) => {
    const now = Date.now();
    const cooldown = SPOKEN_COOLDOWNS[vType] || 10000;
    const isFirstTime = !firstOccurrenceRef.current[vType];

    // First occurrence of any violation category speaks immediately without delay!
    // Subsequent occurrences enforce a polite cooldown to avoid speech overlap
    if (!isFirstTime && lastSpokenRef.current[vType] && (now - lastSpokenRef.current[vType] < cooldown)) {
      return;
    }
    firstOccurrenceRef.current[vType] = true;
    lastSpokenRef.current[vType] = now;
    lastInteractionTimeRef.current = now;

    // 1. Dispatch directly to Beyond Presence Avatar iframe via multiple supported protocols
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        const targetWin = iframeRef.current.contentWindow;
        targetWin.postMessage({ type: 'speak', text: spokenText }, '*');
        targetWin.postMessage({ type: 'say', text: spokenText }, '*');
        targetWin.postMessage({ action: 'speak', payload: { text: spokenText } }, '*');
        targetWin.postMessage({
          type: 'user_message',
          message: isWarning
            ? `[PROCTORING INTERVENTION - PLEASE SPEAK ALOUD IMMEDIATELY TO CANDIDATE]: ${spokenText}`
            : `[PLEASE SPEAK ALOUD TO CANDIDATE]: ${spokenText}`
        }, '*');
        if (isWarning) {
          targetWin.postMessage({
            type: 'proctor_alert',
            violationType: vType,
            message: spokenText
          }, '*');
        }
      } catch (err) {
        console.warn('[AvatarModal] postMessage to avatar notice:', err);
      }
    }

    // 2. Dispatch intervention to backend (logs to session transcript as [AI Interviewer (Proctor)]
    //    and triggers Beyond Presence live call API if call_id is active)
    try {
      api.post('/proctoring/intervene', {
        session_id: currentSessionId,
        call_id: beyondCallIdRef.current,
        violation_type: vType,
        spoken_warning: spokenText
      }).catch(() => {});
    } catch (_) {}

    // 3. Record intervention in transcript
    const speakerLabel = isWarning ? 'AI Interviewer (Proctor)' : aiSpeakerLabel;
    recordTranscriptLine(speakerLabel, spokenText);
  }, [currentSessionId, aiSpeakerLabel, recordTranscriptLine]);

  // Capture snapshot from live candidate camera
  const captureWebcamSnapshot = useCallback(() => {
    try {
      const video = videoRef.current;
      if (video && video.videoWidth > 0 && video.videoHeight > 0) {
        const snapCanvas = document.createElement('canvas');
        snapCanvas.width = 320;
        snapCanvas.height = 240;
        const ctx = snapCanvas.getContext('2d');
        ctx.drawImage(video, 0, 0, snapCanvas.width, snapCanvas.height);
        return snapCanvas.toDataURL('image/jpeg', 0.55);
      }
    } catch (err) {
      console.warn('Proctoring snapshot capture error:', err);
    }
    return null;
  }, []);

  const recordViolation = useCallback((type, message, spokenMessage = null, deduction = 8, preCapturedFrame = null) => {
    // DO NOT deduct points, log violations, or trigger interventions until conversation has officially started!
    if (!isInterviewStartedRef.current) {
      return;
    }

    const timestamp = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const snapshot = preCapturedFrame || captureWebcamSnapshot();

    const violationObj = {
      type,
      message,
      timestamp,
      isoTimestamp: new Date().toISOString(),
      snapshot
    };

    setProctoringEvents(prev => {
      const updated = [...prev.slice(-29), violationObj];
      proctoringEventsRef.current = updated;
      return updated;
    });
    proctoringEventsRef.current = [...(proctoringEventsRef.current || []).slice(-29), violationObj];

    // Any warning is delivered by the AI Interviewer only (spoken directly to candidate):
    if (spokenMessage) {
      speakAvatarInterviewer(spokenMessage, type, true);
    }

    // Update metrics and recalculate integrity score
    setProctoringMetrics(prev => {
      const next = { ...prev };
      if (type === 'TAB_SWITCH') next.tabSwitches += 1;
      else if (type === 'NO_PERSON_DETECTED') next.faceAbsence += 1;
      else if (type === 'MULTIPLE_PERSONS_DETECTED') next.multipleFaces += 1;
      else if (type === 'PHONE_DETECTED') next.phoneDetected += 1;
      else if (type === 'HEAD_MOVEMENT_AWAY') next.headTurns += 1;
      else if (type === 'FULLSCREEN_EXIT') next.fullscreenExits += 1;
      else if (type === 'SCREEN_SHARE_STOPPED') next.screenShareStops += 1;
      else if (type === 'MULTI_MONITOR_DETECTED') next.multiMonitorAlerts += 1;

      const totalDeduction = (next.tabSwitches * 12) +
                             (next.phoneDetected * 15) +
                             (next.multipleFaces * 12) +
                             (next.faceAbsence * 8) +
                             (next.headTurns * 4) +
                             (next.fullscreenExits * 5) +
                             (next.screenShareStops * 15) +
                             (next.multiMonitorAlerts * 10);

      const calculatedScore = Math.max(0, 100 - totalDeduction);
      integrityScoreRef.current = calculatedScore;
      proctoringMetricsRef.current = next;
      setIntegrityScore(calculatedScore);
      return next;
    });
  }, [captureWebcamSnapshot, speakAvatarInterviewer]);

  // Start Screen Sharing stream
  const startScreenShare = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        alert('Screen sharing is not supported by your browser.');
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always', displaySurface: 'monitor' },
        audio: false
      });

      screenStreamRef.current = stream;
      if (screenVideoRef.current) {
        screenVideoRef.current.srcObject = stream;
      }
      setIsScreenSharing(true);

      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          setIsScreenSharing(false);
          recordViolation(
            'SCREEN_SHARE_STOPPED',
            'Screen sharing was stopped! Please re-share your entire screen.',
            'Candidate warning: Screen sharing was stopped. Please share your entire screen immediately to continue your assessment.',
            15
          );
        };
      }
    } catch (err) {
      console.warn('Screen share initiation notice:', err);
    }
  }, [recordViolation]);

  // Request fullscreen mode
  const requestFullscreen = useCallback(() => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } catch (_) {}
  }, []);

  // Auto-request fullscreen & monitor fullscreen state
  useEffect(() => {
    if (!showAvatar || mode !== 'interviewer') return;

    const handleFullscreenChange = () => {
      const inFullscreen = !!document.fullscreenElement;
      setIsFullscreen(inFullscreen);
      if (!inFullscreen) {
        recordViolation(
          'FULLSCREEN_EXIT',
          'Fullscreen exited! Return to fullscreen mode for proctoring compliance.',
          'Candidate warning: Fullscreen mode exited. Please return to fullscreen.',
          5
        );
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    requestFullscreen();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (document.fullscreenElement) {
        try { document.exitFullscreen().catch(() => {}); } catch (_) {}
      }
    };
  }, [showAvatar, mode, recordViolation, requestFullscreen]);

  // Tab switch & Window focus detection
  useEffect(() => {
    if (!showAvatar || mode !== 'interviewer') return;

    const handleVisibilityChange = () => {
      if (!isInterviewStartedRef.current) return;
      if (document.hidden) {
        recordViolation(
          'TAB_SWITCH',
          'Tab switch detected! External tabs are strictly monitored during interview.',
          'Candidate warning: Tab switch detected. External navigation is strictly prohibited.',
          12
        );
      }
    };

    const handleWindowBlur = () => {
      if (!isInterviewStartedRef.current) return;

      // When candidate clicks into the avatar iframe, the parent window fires a blur event.
      // Wait a tick to check if focus transferred to the iframe so interacting with the avatar is never penalized.
      setTimeout(() => {
        if (
          document.activeElement === iframeRef.current ||
          document.activeElement?.tagName === 'IFRAME' ||
          (iframeRef.current && iframeRef.current.contains(document.activeElement))
        ) {
          return;
        }

        recordViolation(
          'TAB_SWITCH',
          'Window focus lost! Please keep your interview window active.',
          'Candidate warning: Window focus lost. Please stay focused on the interview.',
          10
        );
      }, 150);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [showAvatar, mode, recordViolation]);

  // Keyboard shortcut & Clipboard lockdown
  useEffect(() => {
    if (!showAvatar || mode !== 'interviewer') return;

    const handleKeyDown = (e) => {
      if (!isInterviewStartedRef.current) return;
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'i', 'J', 'j', 'C', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['u', 'U'].includes(e.key))
      ) {
        e.preventDefault();
        recordViolation(
          'DEVTOOLS_BLOCKED',
          'Developer tools shortcut blocked!',
          'Developer inspection shortcuts are disabled during this assessment.',
          8
        );
        return false;
      }

      if (e.ctrlKey && ['c', 'C', 'v', 'V', 'x', 'X', 'a', 'A'].includes(e.key)) {
        e.preventDefault();
        recordViolation(
          'CLIPBOARD_BLOCKED',
          'Copy & Paste is disabled during this interview assessment.',
          'Copy and paste operations are restricted.',
          5
        );
        return false;
      }
    };

    const blockEvent = (e) => e.preventDefault();

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('copy', blockEvent);
    window.addEventListener('paste', blockEvent);
    window.addEventListener('contextmenu', blockEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('copy', blockEvent);
      window.removeEventListener('paste', blockEvent);
      window.removeEventListener('contextmenu', blockEvent);
    };
  }, [showAvatar, mode, recordViolation]);

  // Webcam Vision Capture (every 3.5s) & Real-time Candidate Monitoring
  useEffect(() => {
    if (!showAvatar || (mode !== 'interviewer' && mode !== 'counsellor')) return;

    let stream = null;
    let captureInterval = null;

    const startWebcam = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 480, height: 360, facingMode: 'user' },
          audio: false
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setIsWebcamActive(true);

        const scanIntervalMs = mode === 'counsellor' ? 4500 : 3500;
        captureInterval = setInterval(async () => {
          if (isAnalyzingFrameRef.current) return;
          if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            if (video.videoWidth > 0 && video.videoHeight > 0) {
              canvas.width = 240;
              canvas.height = 180;
              context.drawImage(video, 0, 0, canvas.width, canvas.height);

              const frameBase64 = canvas.toDataURL('image/jpeg', 0.45);
              isAnalyzingFrameRef.current = true;

              try {
                const res = await api.post('/proctoring/analyze-frame', { frame: frameBase64 });
                if (res.data) {
                  // Multi-frame temporal confirmation for phone detection:
                  // Real phone usage persists across consecutive scans (~7+ seconds),
                  // whereas transient camera noise, clothing folds, or lighting shifts only trigger on a single frame.
                  const rawPhone = !!res.data.phone_detected;
                  if (rawPhone) {
                    consecutivePhoneFramesRef.current += 1;
                  } else {
                    consecutivePhoneFramesRef.current = 0;
                  }
                  // Require at least 2 consecutive positive frame detections
                  const confirmedPhone = consecutivePhoneFramesRef.current >= 2;

                  // Update real-time vision HUD tracking state
                  setVisionState({
                    faceCount: typeof res.data.face_count === 'number' ? res.data.face_count : 1,
                    faceBox: res.data.face_box || null,
                    gazeDirection: res.data.gaze_direction || 'CENTER',
                    phoneDetected: confirmedPhone
                  });

                  if (mode === 'interviewer' && res.data.violation_detected) {
                    const vType = res.data.violation_type || 'VISUAL_ANOMALY';
                    const vMsg = res.data.message || 'Proctoring alert detected!';
                    const spoken = res.data.avatar_spoken_warning ||
                      (vType === 'PHONE_DETECTED'
                        ? 'Candidate warning: A mobile phone was detected in your workspace. Please put your phone away immediately. Phone usage is strictly prohibited.'
                        : vType === 'NO_PERSON_DETECTED'
                        ? 'Candidate warning: Face not visible in camera. Please remain centered in front of the camera.'
                        : vType === 'MULTIPLE_PERSONS_DETECTED'
                        ? 'Candidate warning: More than one person has been detected in your camera frame. This interview must be conducted alone in a private room. Please ask anyone else to leave immediately.'
                        : vType === 'HEAD_MOVEMENT_AWAY' && (vMsg.toLowerCase().includes('down') || res.data.gaze_direction === 'AWAY')
                        ? 'Candidate warning: You are looking down away from the screen. Please keep your eyes focused on the interviewer and stay concentrated.'
                        : 'Candidate warning: Please maintain your concentration on the interview screen. Looking away repeatedly is flagged by our visual proctoring system.');

                    // Strict anti-flicker guard: only penalize and warn for phone if confirmed across consecutive frames
                    if (vType === 'PHONE_DETECTED') {
                      if (confirmedPhone) {
                        recordViolation(vType, vMsg, spoken, 15, frameBase64);
                      }
                    } else {
                      recordViolation(vType, vMsg, spoken, 8, frameBase64);
                    }
                  } else if (mode === 'counsellor' && isInterviewStartedRef.current) {
                    if (confirmedPhone) {
                      speakAvatarInterviewer(
                        "I notice you might be checking your phone — feel free to set it aside so we can focus together on your career guidance!",
                        'COUNSELLOR_ENGAGEMENT',
                        false
                      );
                    } else if (res.data.gaze_direction === 'AWAY') {
                      speakAvatarInterviewer(
                        "Take all the time you need to think — whenever you're ready, feel free to share your thoughts.",
                        'COUNSELLOR_ENGAGEMENT',
                        false
                      );
                    }
                  }
                }
              } catch (err) {
                // Silently ignore transient network failures
              } finally {
                isAnalyzingFrameRef.current = false;
              }
            }
          }
        }, scanIntervalMs);
      } catch (err) {
        console.warn('Failed to access webcam for vision monitoring:', err);
        setIsWebcamActive(false);
      }
    };

    startWebcam();

    return () => {
      if (captureInterval) clearInterval(captureInterval);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      setIsWebcamActive(false);
    };
  }, [showAvatar, mode, recordViolation, speakAvatarInterviewer]);

  // Real-time Cyber Face-Tracking Canvas Overlay on Candidate Webcam
  useEffect(() => {
    if (!overlayCanvasRef.current || !videoRef.current || webcamMinimized) return;
    const canvas = overlayCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    if (visionState.faceBox && visionState.faceCount > 0) {
      const [bx, by, bw, bh] = visionState.faceBox;
      const x = bx * w;
      const y = by * h;
      const width = bw * w;
      const height = bh * h;

      const isDanger = visionState.phoneDetected || visionState.faceCount > 1;
      const isWarning = visionState.gazeDirection === 'AWAY';
      const strokeColor = isDanger ? '#f43f5e' : isWarning ? '#f59e0b' : '#10b981';

      // Draw high-tech corner brackets around detected face
      const bracketLen = Math.max(8, Math.min(18, width * 0.22));
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2.5;
      ctx.shadowColor = strokeColor;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      // Top-Left
      ctx.moveTo(x, y + bracketLen);
      ctx.lineTo(x, y);
      ctx.lineTo(x + bracketLen, y);
      // Top-Right
      ctx.moveTo(x + width - bracketLen, y);
      ctx.lineTo(x + width, y);
      ctx.lineTo(x + width, y + bracketLen);
      // Bottom-Right
      ctx.moveTo(x + width, y + height - bracketLen);
      ctx.lineTo(x + width, y + height);
      ctx.lineTo(x + width - bracketLen, y + height);
      // Bottom-Left
      ctx.moveTo(x + bracketLen, y + height);
      ctx.lineTo(x, y + height);
      ctx.lineTo(x, y + height - bracketLen);
      ctx.stroke();

      // Center crosshair
      ctx.shadowBlur = 0;
      ctx.lineWidth = 1;
      const cx = x + width / 2;
      const cy = y + height / 2;
      ctx.beginPath();
      ctx.moveTo(cx - 5, cy);
      ctx.lineTo(cx + 5, cy);
      ctx.moveTo(cx, cy - 5);
      ctx.lineTo(cx, cy + 5);
      ctx.stroke();
    }
  }, [visionState, webcamMinimized]);

  // Screen Share Analysis Capture (every 4.5s when active)
  useEffect(() => {
    if (!showAvatar || mode !== 'interviewer' || !isScreenSharing) return;

    const screenInterval = setInterval(async () => {
      if (isAnalyzingScreenRef.current) return;
      if (screenVideoRef.current && screenCanvasRef.current) {
        const video = screenVideoRef.current;
        const canvas = screenCanvasRef.current;
        const ctx = canvas.getContext('2d');

        if (video.videoWidth > 0 && video.videoHeight > 0) {
          canvas.width = 400;
          canvas.height = Math.round((video.videoHeight / video.videoWidth) * 400);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const screenBase64 = canvas.toDataURL('image/jpeg', 0.45);
          isAnalyzingScreenRef.current = true;

          try {
            const res = await api.post('/proctoring/analyze-screen', { frame: screenBase64 });
            if (res.data && res.data.violation_detected) {
              const vType = res.data.violation_type || 'SCREEN_ANOMALY';
              const vMsg = res.data.message || 'Screen sharing anomaly detected!';
              const spoken = 'Candidate warning: An unauthorized display configuration was detected on your shared screen.';
              recordViolation(vType, vMsg, spoken, 10);
            }
          } catch (_) {
          } finally {
            isAnalyzingScreenRef.current = false;
          }
        }
      }
    }, 4500);

    return () => clearInterval(screenInterval);
  }, [showAvatar, mode, isScreenSharing, recordViolation]);

  // Beyond Presence Avatar postMessage events listener
  useEffect(() => {
    if (!showAvatar || !currentSessionId) return;

    const handleAvatarMessage = (event) => {
      try {
        let payload = event.data;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch (e) {}
        }
        if (!payload) return;

        if (payload.callId) {
          beyondCallIdRef.current = payload.callId;
        } else if (payload.call_id) {
          beyondCallIdRef.current = payload.call_id;
        } else if (payload.data?.callId) {
          beyondCallIdRef.current = payload.data.callId;
        }

        const evt = String(payload.type || payload.event || payload.action || '').toLowerCase().trim();
        
        // Teardown, departure, or session-ending events from the iframe
        const isTeardownEvt = (
          evt === 'call_ended' ||
          evt === 'call_end' ||
          evt === 'conversation_ended' ||
          evt === 'conversation_end' ||
          evt === 'leave' ||
          evt === 'left_meeting' ||
          evt === 'session_ended' ||
          evt === 'closed' ||
          evt === 'disconnected'
        );

        if (isTeardownEvt) {
          // Beyond Presence renders its own "You left the meeting" + feedback page
          // the moment the call ends. Replace the iframe with a progress panel
          // immediately and finish through the same path as the End button, so the
          // student only ever sees the report.
          //
          // 'leave' is our own request and 'disconnected' is frequently transient
          // (the player reconnects), so neither is treated as the end.
          if (evt === 'leave' || evt === 'disconnected') return;
          setIframeHidden(true);
          if (!isEndingSessionRef.current) {
            handleCloseRef.current?.();
          }
          return;
        }

        // Strictly identify conversation commencement events (triggered when Beyond Presence starts call or avatar begins speaking)
        const isStrictStartEvt = !isInterviewStartedRef.current && (
          evt === 'call_started' ||
          evt === 'call_start' ||
          evt === 'conversation_started' ||
          evt === 'conversation_start' ||
          evt === 'agent_speaking' ||
          evt === 'bot_speaking' ||
          evt === 'avatar_speaking' ||
          evt === 'ai_speaking'
        );

        if (isStrictStartEvt) {
          setIsInterviewStarted(true);
        }

        // Set of system/lifecycle keywords that are not spoken dialogue
        const IGNORED_SYSTEM_WORDS = new Set([
          'ready', 'connected', 'connecting', 'disconnected', 'started',
          'player_started', 'player_ready', 'stream_ready', 'camera_ready',
          'mic_ready', 'ok', 'ping', 'pong', 'init', 'initialized',
          'session_ready', 'heartbeat', 'status', 'loaded', 'closed',
          'user_joined', 'agent_joined', 'participant_joined', 'mute', 'unmute',
          'session_started', 'session_start'
        ]);

        // Only parse text if it is explicitly a transcript/speech event or contains speaker/role metadata
        const isTranscriptEvt = (
          evt === 'transcript' ||
          evt === 'user_transcript' ||
          evt === 'agent_transcript' ||
          evt === 'bot_transcript' ||
          evt === 'speech_recognized' ||
          evt === 'speech' ||
          Boolean(payload.transcript || payload.detail?.transcript || payload.data?.transcript || payload.payload?.transcript) ||
          Boolean(payload.role || payload.speaker || payload.data?.role || payload.detail?.role)
        );

        if (!isTranscriptEvt && IGNORED_SYSTEM_WORDS.has(evt)) {
          return;
        }

        const rawText =
          payload.transcript ||
          (isTranscriptEvt ? payload.text : null) ||
          (isTranscriptEvt && !IGNORED_SYSTEM_WORDS.has(String(payload.message || '').toLowerCase().trim()) ? payload.message : null) ||
          payload.speech ||
          payload.detail?.transcript ||
          (isTranscriptEvt ? payload.detail?.text : null) ||
          payload.data?.transcript ||
          (isTranscriptEvt ? payload.data?.text : null) ||
          payload.payload?.transcript ||
          (isTranscriptEvt ? payload.payload?.text : null);

        if (rawText && typeof rawText === 'string') {
          const cleanText = rawText.trim();
          const lowerClean = cleanText.toLowerCase();

          if (cleanText.length >= 3 && !IGNORED_SYSTEM_WORDS.has(lowerClean)) {
            const isUser =
              payload.role === 'user' ||
              payload.speaker === 'user' ||
              payload.data?.role === 'user' ||
              payload.detail?.role === 'user' ||
              payload.type === 'user_transcript';

            // Only AI avatar speech (the greeting) starts the conversation automatically.
            // Ambient student microphone noise before clicking Start Conversation will NOT start the interview.
            if (!isUser && !isInterviewStartedRef.current) {
              setIsInterviewStarted(true);
            }

            if (!isUser) {
              // The agent is taking its turn: the inactivity clock is paused while
              // it speaks, so its own question time is never charged to the student.
              lastAgentActivityRef.current = Date.now();
            }

            if (isInterviewStartedRef.current) {
              lastInteractionTimeRef.current = Date.now();
              silencePhaseRef.current = 0;
              if (isUser) {
                // Only a real student answer resets the 2-minute rule.
                inactivityStateRef.current = resetInactivity(inactivityStateRef.current);
                setInactivityCountdown((prev) => (prev === null ? prev : null));
              }
            }

            const speakerName = isUser ? 'Student' : aiSpeakerLabel;
            if (isInterviewStartedRef.current || !isUser) {
              recordTranscriptLine(speakerName, cleanText);
            }
          }
        }
      } catch (err) {
        console.warn('[AvatarModal] Message listener notice:', err);
      }
    };

    window.addEventListener('message', handleAvatarMessage);
    return () => {
      window.removeEventListener('message', handleAvatarMessage);
    };
  }, [showAvatar, currentSessionId, mode, sessionMeta, aiSpeakerLabel, recordTranscriptLine, setIsInterviewStarted]);

  // Proactive silence watchdog: AI Interviewer & AI Counsellor will not stay quiet if candidate is silent
  useEffect(() => {
    if (!showAvatar || !isInterviewStarted || (mode !== 'interviewer' && mode !== 'counsellor')) return;

    lastInteractionTimeRef.current = Date.now();
    silencePhaseRef.current = 0;

    const silenceCheckInterval = setInterval(() => {
      if (isEndingSession) return;

      const elapsed = (Date.now() - lastInteractionTimeRef.current) / 1000;

      if (mode === 'interviewer') {
        // Interviewer Phase 1: After 14 seconds of silence, gently nudge candidate
        if (elapsed >= 14 && silencePhaseRef.current === 0) {
          silencePhaseRef.current = 1;
          lastInteractionTimeRef.current = Date.now();
          const nudge = "Take your time to structure your thoughts. Whenever you are ready, please share your thoughts, or let me know if you would like me to clarify the question.";
          speakAvatarInterviewer(nudge, 'SILENCE_NUDGE', false);
        }
        // Interviewer Phase 2: After another 15 seconds (29s total), offer hint or rephrase
        else if (elapsed >= 15 && silencePhaseRef.current === 1) {
          silencePhaseRef.current = 2;
          lastInteractionTimeRef.current = Date.now();
          const nudge = "Are you still with me? If you need a hint or would prefer to move forward to the next question, please let me know.";
          speakAvatarInterviewer(nudge, 'SILENCE_NUDGE', false);
        }
        // Interviewer Phase 3: After another 15 seconds (44s total), gracefully advance to next question
        else if (elapsed >= 15 && silencePhaseRef.current === 2) {
          silencePhaseRef.current = 3;
          lastInteractionTimeRef.current = Date.now();
          const advance = "No problem at all! Let's keep the momentum going and proceed to our next question.";
          speakAvatarInterviewer(advance, 'SILENCE_ADVANCE', false);

          if (iframeRef.current && iframeRef.current.contentWindow) {
            try {
              iframeRef.current.contentWindow.postMessage({
                type: 'user_message',
                message: "[Candidate was silent for an extended period. Please acknowledge and ask the next interview question.]"
              }, '*');
            } catch (_) {}
          }
        }
      } else if (mode === 'counsellor') {
        // Counsellor Phase 1: After 15 seconds of silence
        if (elapsed >= 15 && silencePhaseRef.current === 0) {
          silencePhaseRef.current = 1;
          lastInteractionTimeRef.current = Date.now();
          const nudge = "I'm here whenever you're ready! Feel free to share your thoughts, or ask any career or academic questions you have in mind.";
          speakAvatarInterviewer(nudge, 'SILENCE_NUDGE', false);
        }
        // Counsellor Phase 2: After another 20 seconds (35s total)
        else if (elapsed >= 20 && silencePhaseRef.current === 1) {
          silencePhaseRef.current = 2;
          lastInteractionTimeRef.current = Date.now();
          const nudge = "Take all the time you need. We can explore career pathways, your resume, or skill development — what would you like to discuss first?";
          speakAvatarInterviewer(nudge, 'SILENCE_NUDGE', false);
        }
        // Counsellor Phase 3: After another 25 seconds (60s total)
        else if (elapsed >= 25 && silencePhaseRef.current === 2) {
          silencePhaseRef.current = 3;
          lastInteractionTimeRef.current = Date.now();
          const nudge = "Whenever you're ready, feel free to tell me what questions or goals you'd like us to focus on today.";
          speakAvatarInterviewer(nudge, 'SILENCE_NUDGE', false);

          if (iframeRef.current && iframeRef.current.contentWindow) {
            try {
              iframeRef.current.contentWindow.postMessage({
                type: 'user_message',
                message: "[Student has been quiet for a moment. Please warmly invite them to share their career aspirations, questions, or resume details.]"
              }, '*');
            } catch (_) {}
          }
        }
      }
    }, 1000);

    return () => clearInterval(silenceCheckInterval);
  }, [showAvatar, isInterviewStarted, mode, isEndingSession, speakAvatarInterviewer]);

  // Microphone voice activity (interviewer only). Soft, slow or short answers
  // often produce no transcript for a long time, and scoring that time as silence
  // ended interviews that were being answered. Only a student who is genuinely
  // absent (no voice, no transcript) can reach the warning.
  useEffect(() => {
    if (!showAvatar || mode !== 'interviewer') return undefined;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return undefined;
    }

    let stream = null;
    let audioContext = null;
    let poll = null;
    let cancelled = false;
    const tracker = createVoiceTracker();
    voiceTrackerRef.current = tracker;

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        audioContext = new AudioContextClass();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        audioContext.createMediaStreamSource(stream).connect(analyser);
        const buffer = new Float32Array(analyser.fftSize);

        poll = setInterval(() => {
          analyser.getFloatTimeDomainData(buffer);
          const result = tracker.feed(readVoiceLevel(buffer), Date.now());
          if (!result.voiced) return;
          // Sustained voice is a response: pause the clock, drop any warning, and
          // silence the older spoken nudges too (they keyed off the transcript, so
          // a slow speaker could be interrupted mid-answer with "are you there?").
          lastStudentVoiceAtRef.current = Date.now();
          lastInteractionTimeRef.current = Date.now();
          silencePhaseRef.current = 0;
          inactivityStateRef.current = resetInactivity(inactivityStateRef.current);
          setInactivityCountdown((prev) => (prev === null ? prev : null));
        }, VOICE_POLL_MS);
      } catch (e) {
        // A denied or unavailable microphone must never break the interview: the
        // transcript remains as the fallback presence signal.
        console.warn('Voice activity detection unavailable:', e);
      }
    };
    start();

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      voiceTrackerRef.current = null;
      if (stream) stream.getTracks().forEach((track) => track.stop());
      if (audioContext && audioContext.state !== 'closed') {
        Promise.resolve(audioContext.close()).catch(() => {});
      }
    };
  }, [showAvatar, mode]);

  // 2-minute inactivity rule (interviewer only). Uses wall-clock deltas so a
  // throttled background tab still records real absence, and skips the advance
  // while the agent is speaking so a long question cannot end the interview.
  useEffect(() => {
    if (!showAvatar || !isInterviewStarted || mode !== 'interviewer') return;

    inactivityStateRef.current = createInactivityState(inactivityConfig);
    lastAgentActivityRef.current = Date.now();
    setInactivityCountdown(null);
    let lastTick = Date.now();

    const inactivityInterval = setInterval(() => {
      const now = Date.now();
      const delta = now - lastTick;
      lastTick = now;

      // Never terminate while the UI is already ending, or after it ended.
      if (isEndingSession || isEndingSessionRef.current) return;
      // Pause for the agent's turn (speaking, processing, or awaiting its reply).
      if (now - lastAgentActivityRef.current < AI_TURN_GRACE_MS) return;
      // Pause while the student is (or has just been) talking, however softly and
      // slowly, so real answers are never charged as silence.
      if (now - lastStudentVoiceAtRef.current < SPEAKING_HOLD_MS) return;

      const next = advanceInactivity(inactivityStateRef.current, delta);
      inactivityStateRef.current = next;

      if (next.warn) {
        setInactivityCountdown(remainingSilenceSeconds(next));
        speakAvatarInterviewer(
          'I have not heard from you for two minutes. Please respond now, otherwise this interview will end automatically and your report will be generated.',
          'SILENCE_WARNING',
          true
        );
      } else if (next.warned && !next.terminate) {
        setInactivityCountdown(remainingSilenceSeconds(next));
      }

      if (next.terminate && !isEndingSessionRef.current) {
        setInactivityCountdown(0);
        // Same single entry point as the button: one guard, one report.
        handleCloseRef.current?.({
          termination: {
            reason: 'inactivity',
            inactivitySeconds: Math.round(next.elapsedMs / 1000)
          }
        });
      }
    }, 1000);

    return () => clearInterval(inactivityInterval);
  }, [showAvatar, isInterviewStarted, mode, isEndingSession, inactivityConfig, speakAvatarInterviewer]);

  const handleClose = useCallback(async (options = {}) => {
    if (isEndingSessionRef.current) return;
    isEndingSessionRef.current = true;
    try {
      setIsEndingSession(true);

      // Hide (and therefore unmount) the Beyond Presence iframe before awaiting
      // the report. BP shows its feedback page as soon as the call ends; the panel
      // rendered in its place explains the wait and then the report appears.
      setIframeHidden(true);
      try {
        iframeRef.current?.contentWindow?.postMessage({ type: 'leave' }, '*');
      } catch (e) {
        // Best effort: a cross-origin teardown notice; unmounting ends the call.
      }

      // Stop screen share tracks
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
      }

      if (mode === 'interviewer' || mode === 'counsellor' || mode === 'tutor') {
        const currentMetrics = proctoringMetricsRef.current || proctoringMetrics;
        const currentIntegrity = typeof integrityScoreRef.current === 'number' ? integrityScoreRef.current : integrityScore;
        const currentViolations = Object.values(currentMetrics).reduce((a, b) => a + b, 0);
        const currentEvents = (proctoringEventsRef.current && proctoringEventsRef.current.length > 0)
          ? proctoringEventsRef.current
          : proctoringEvents;

        const proctoringSummary = {
          integrity_score: currentIntegrity,
          total_violations: currentViolations,
          status: currentIntegrity >= 80 ? 'HIGH_INTEGRITY' : currentIntegrity >= 60 ? 'MODERATE' : 'FLAGGED',
          breakdown: { ...currentMetrics },
          events: currentEvents,
          events_count: currentEvents.length,
          screen_share_used: isScreenSharing
        };

        const report = await finalizeSession(
          beyondCallIdRef.current,
          mode === 'interviewer'
            ? {
                visualViolations: currentViolations,
                proctoring: proctoringSummary,
                ...(options.termination ? { termination: options.termination } : {})
              }
            : {}
        );

        if (onSessionEnded) {
          onSessionEnded(report);
        }
      }
    } catch (e) {
      console.warn('Error ending session:', e);
    } finally {
      isEndingSessionRef.current = false;
      setIsEndingSession(false);
      setShowAvatar(false);
      if (setFormData) setFormData({ topic: '', resume: null });
      if (setSubmitted) setSubmitted(false);
    }
  }, [finalizeSession, isScreenSharing, mode, onSessionEnded, proctoringEvents, proctoringMetrics, setFormData, setSubmitted, setShowAvatar]);

  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  if (!showAvatar) return null;

  return (
    <div className="avatar-modal-overlay">
      {/* Hidden canvas for frame base64 encoding to backend YuNet vision model */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Hidden elements for Screen Share Analytics */}
      <video ref={screenVideoRef} autoPlay playsInline muted style={{ display: 'none' }} />
      <canvas ref={screenCanvasRef} style={{ display: 'none' }} />

      <div className="avatar-modal-container">
        {sessionExpiredNotice && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              margin: '0 0 8px 0',
              borderRadius: '10px',
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              color: '#92400e',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{sessionExpiredNotice}</span>
            <button
              type="button"
              onClick={handleClose}
              style={{
                background: '#b45309',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              End &amp; view report
            </button>
          </div>
        )}

        <div className="avatar-header">
          <div className="avatar-title-group">
            <span className="avatar-status-dot" />
            <h3>{aiSpeakerLabel}</h3>
          </div>

          {/* AI Proctoring Live HUD */}
          {mode === 'interviewer' && (
            <div className="avatar-proctoring-hud">
              <div className={`avatar-proctoring-badge ${!isInterviewStarted ? 'standby' : 'clean'}`}>
                <span className="proctoring-pulse-dot" />
                <span className="proctoring-label">{isInterviewStarted ? 'PROCTORING ACTIVE' : 'PROCTORING STANDBY'}</span>
              </div>

              {!isInterviewStarted && (
                <button
                  type="button"
                  onClick={() => setIsInterviewStarted(true)}
                  style={{
                    background: '#16a34a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap'
                  }}
                  title="Click here if you have started the conversation in the avatar"
                >
                  <CheckCircle2 size={12} /> Started Conversation
                </button>
              )}

              <div className="avatar-integrity-pill" style={!isInterviewStarted ? { opacity: 0.45, filter: 'grayscale(1)' } : {}}>
                {!isInterviewStarted ? (
                  <ShieldCheck size={14} style={{ color: '#94a3b8' }} />
                ) : integrityScore >= 80 ? (
                  <ShieldCheck size={14} style={{ color: '#34d399' }} />
                ) : (
                  <ShieldAlert size={14} style={{ color: '#f87171' }} />
                )}
                <span>
                  Integrity:{' '}
                  <strong>{isInterviewStarted ? `${integrityScore}%` : 'Standby'}</strong>
                </span>
              </div>

              {/* Screen Sharing Toggle & Status */}
              {isScreenSharing ? (
                <div className="avatar-screenshare-pill" title="Screen sharing is currently monitored">
                  <CheckCircle2 size={13} /> Screen Active
                </div>
              ) : (
                <button
                  className="avatar-screenshare-btn"
                  onClick={startScreenShare}
                  title="Share your entire screen for proctoring verification"
                >
                  <ScreenShare size={13} /> Share Screen
                </button>
              )}

              {isInterviewStarted && totalViolations > 0 && (
                <div className="avatar-violation-count-pill">
                  <AlertTriangle size={13} />
                  <span>Violations: {totalViolations}</span>
                </div>
              )}

              {/* Live Network RTT Ping Badge */}
              <div
                className={`avatar-network-ping-pill ${livePingStatus}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: livePingStatus === 'optimal' ? 'rgba(34, 197, 94, 0.15)' : livePingStatus === 'fair' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.2)',
                  color: livePingStatus === 'optimal' ? '#4ade80' : livePingStatus === 'fair' ? '#facc15' : '#f87171',
                  border: `1px solid ${livePingStatus === 'optimal' ? 'rgba(34, 197, 94, 0.35)' : livePingStatus === 'fair' ? 'rgba(234, 179, 8, 0.35)' : 'rgba(239, 68, 68, 0.4)'}`
                }}
                title="Live Network Round Trip Time (RTT). WebRTC requires low latency (<150ms) for smooth real-time AI avatar responses."
              >
                <Wifi size={12} />
                <span>{livePing !== null ? `${livePing} ms` : 'Ping...'}</span>
              </div>

              {!isFullscreen && (
                <button
                  className="avatar-fullscreen-btn"
                  onClick={requestFullscreen}
                  title="Enable Fullscreen Proctoring Mode"
                >
                  <Maximize2 size={13} /> Fullscreen
                </button>
              )}
            </div>
          )}

          {/* AI Counsellor Behaviour & Engagement Live HUD */}
          {mode === 'counsellor' && (
            <div className="avatar-proctoring-hud">
              <div className="avatar-proctoring-badge clean" style={{ background: 'rgba(59, 130, 246, 0.15)', borderColor: 'rgba(59, 130, 246, 0.4)' }}>
                <span className="proctoring-pulse-dot" style={{ background: '#60a5fa', boxShadow: '0 0 8px #60a5fa' }} />
                <span className="proctoring-label" style={{ color: '#93c5fd' }}>AI BEHAVIOUR & ENGAGEMENT: ACTIVE</span>
              </div>

              {/* Live Network RTT Ping Badge */}
              <div
                className={`avatar-network-ping-pill ${livePingStatus}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: livePingStatus === 'optimal' ? 'rgba(34, 197, 94, 0.15)' : livePingStatus === 'fair' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.2)',
                  color: livePingStatus === 'optimal' ? '#4ade80' : livePingStatus === 'fair' ? '#facc15' : '#f87171',
                  border: `1px solid ${livePingStatus === 'optimal' ? 'rgba(34, 197, 94, 0.35)' : livePingStatus === 'fair' ? 'rgba(234, 179, 8, 0.35)' : 'rgba(239, 68, 68, 0.4)'}`
                }}
                title="Live Network Round Trip Time (RTT). WebRTC requires low latency (<150ms) for smooth real-time AI avatar responses."
              >
                <Wifi size={12} />
                <span>{livePing !== null ? `${livePing} ms` : 'Ping...'}</span>
              </div>
            </div>
          )}

          <button
            id="avatar-end-btn"
            className="avatar-close-btn"
            onClick={handleClose}
            disabled={isEndingSession}
          >
            {isEndingSession ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Finalizing AI Evaluation...
              </>
            ) : mode === 'interviewer' ? (
              <>
                <X size={18} /> End Interview & Get Report
              </>
            ) : (
              <>
                <X size={18} /> End Session
              </>
            )}
          </button>
        </div>

        <div className="avatar-modal-body" style={{ position: 'relative' }}>
          {/* High Latency Floating Banner Notice */}
          {livePingStatus === 'high' && (
            <div style={{
              position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
              zIndex: 100, background: 'rgba(239, 68, 68, 0.92)', color: '#ffffff',
              padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
              backdropFilter: 'blur(4px)', pointerEvents: 'none'
            }}>
              <Wifi size={14} />
              <span>High network ping ({livePing} ms) — WebRTC avatar responses may experience latency</span>
            </div>
          )}
          {/* Inactivity warning: 2 minutes of student silence, agent turns excluded */}
          {mode === 'interviewer' && inactivityCountdown !== null && !isEndingSession && (
            <div
              role="alert"
              data-testid="inactivity-warning"
              style={{
                position: 'absolute', top: '56px', left: '50%', transform: 'translateX(-50%)',
                zIndex: 101, background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e',
                padding: '10px 16px', borderRadius: '12px', fontSize: '12.5px', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '560px',
                boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
              }}
            >
              <AlertTriangle size={16} color="#b45309" />
              <span>
                We can&apos;t hear you. Please respond — this interview ends automatically in {inactivityCountdown}s and your report will record the inactivity.
              </span>
            </div>
          )}
          <div className="avatar-iframe-wrapper">
            {iframeHidden || isEndingSession ? (
              <div
                data-testid="avatar-teardown"
                style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '24px',
                  textAlign: 'center', background: '#0b1220', color: '#e2e8f0'
                }}
              >
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', border: '3px solid rgba(148,163,184,0.25)', borderTopColor: '#3b82f6' }} />
                <div style={{ fontSize: '15px', fontWeight: '700' }}>Interview ended</div>
                <div style={{ fontSize: '12.5px', color: '#94a3b8', maxWidth: '360px', lineHeight: '1.5' }}>
                  Generating and saving your AI evaluation report. This page switches to your report as soon as it is saved.
                </div>
              </div>
            ) : lockedIframeSrc || avatarUrl ? (
              <iframe
                ref={iframeRef}
                src={lockedIframeSrc || (avatarUrl.includes('?') ? `${avatarUrl}&external_id=${currentSessionId}` : `${avatarUrl}?external_id=${currentSessionId}`)}
                title="Beyond Presence Avatar"
                allow="camera; microphone; autoplay; encrypted-media; display-capture"
                className="avatar-iframe"
              />
            ) : (
              <div className="avatar-config-warning">
                <h4>Avatar agent is not configured</h4>
                <p>
                  Please ensure you have configured valid API keys.
                </p>
              </div>
            )}
          </div>


          {/* Candidate Webcam Monitor (Picture-in-Picture) with AI Vision Reticle */}
          {(mode === 'interviewer' || mode === 'counsellor') && (
            <div className={`avatar-webcam-pip-container ${webcamMinimized ? 'minimized' : ''}`} title="Live Candidate Camera (AI Vision Monitored)">
              <div className="avatar-webcam-pip-header">
                <div className="avatar-webcam-tag">
                  <span className={`webcam-live-dot ${isWebcamActive ? 'active' : 'inactive'}`} />
                  <Camera size={11} />
                  <span>{mode === 'counsellor' ? 'AI Engagement: Monitored' : 'AI Vision: Monitored'}</span>
                </div>
                <button
                  type="button"
                  className="avatar-webcam-toggle-btn"
                  onClick={() => setWebcamMinimized(prev => !prev)}
                  title={webcamMinimized ? "Expand Camera Monitor" : "Minimize Camera Monitor"}
                >
                  {webcamMinimized ? <Maximize2 size={11} /> : <Minimize2 size={11} />}
                </button>
              </div>

              <div className="avatar-webcam-video-wrapper">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="avatar-webcam-video"
                />
                <canvas
                  ref={overlayCanvasRef}
                  className="avatar-webcam-overlay"
                  width={320}
                  height={240}
                />

                {/* Live AI Vision Status Badge */}
                <div className={`avatar-webcam-status-pill ${
                  visionState.phoneDetected
                    ? (mode === 'counsellor' ? 'status-warning' : 'status-danger')
                    : visionState.faceCount === 0
                    ? 'status-danger'
                    : visionState.faceCount > 1
                    ? 'status-warning'
                    : visionState.gazeDirection === 'AWAY'
                    ? 'status-warning'
                    : 'status-clean'
                }`}>
                  {visionState.phoneDetected ? (
                    mode === 'counsellor' ? 'Phone In View: Stay Focused' : 'Phone Detected! Remove Device'
                  ) : visionState.faceCount === 0 ? (
                    'Face Not In Frame'
                  ) : visionState.faceCount > 1 ? (
                    'Multiple Faces Detected'
                  ) : visionState.gazeDirection === 'AWAY' ? (
                    mode === 'counsellor' ? 'Taking a Moment / Looking Away' : 'Lack of Concentration: Looking Away'
                  ) : (
                    mode === 'counsellor' ? 'Engaged & Attentive' : 'Focused on Interview'
                  )}
                </div>
              </div>

              {!webcamMinimized && (
                <div className="avatar-webcam-footer">
                  <div className="avatar-webcam-audio-indicator">
                    <Mic size={10} className="audio-live-icon" />
                    <span>{mode === 'counsellor' ? 'Mic Active' : 'Mic Monitored'}</span>
                  </div>
                  <span className="avatar-webcam-ai-tag">{mode === 'counsellor' ? 'Behaviour AI' : 'YuNet Face AI'}</span>
                </div>
              )}
            </div>
          )}

          {/* Fallback hidden video element for non-camera modes */}
          {mode !== 'interviewer' && mode !== 'counsellor' && (
            <video ref={videoRef} autoPlay playsInline muted style={{ display: 'none' }} />
          )}

          {/* Picture-in-Picture Floating Miniature Screen Preview */}
          {isScreenSharing && (
            <div className="avatar-screen-pip-container" title="Live Shared Screen (Proctored)">
              <div className="avatar-screen-pip-tag">
                <ScreenShare size={10} /> Proctored Screen
              </div>
              <video
                ref={(el) => {
                  if (el && screenStreamRef.current) {
                    el.srcObject = screenStreamRef.current;
                  }
                }}
                autoPlay
                playsInline
                muted
                className="avatar-screen-pip-video"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvatarModal;
